import { useEffect, useMemo, useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

type Familias = {
  icms: boolean;
  ipi: boolean;
  pisCofins: boolean;
  fcp: boolean;
  mvaSt: boolean;
  cbsIbs: boolean;
  iss: boolean;
};

const FAMILIAS_INICIAIS: Familias = {
  icms: false,
  ipi: false,
  pisCofins: false,
  fcp: false,
  mvaSt: false,
  cbsIbs: false,
  iss: false,
};

const FAMILIAS_FISCAIS = [
  { chave: "icms", titulo: "ICMS", contagem: "icms" },
  { chave: "ipi", titulo: "IPI", contagem: "ipi" },
  { chave: "pisCofins", titulo: "PIS / COFINS", contagem: "pisCofins" },
  { chave: "fcp", titulo: "FCP", contagem: "fcp" },
  { chave: "mvaSt", titulo: "MVA-ST", contagem: "mvaSt" },
  { chave: "cbsIbs", titulo: "CBS / IBS", contagem: "cbsIbs" },
  { chave: "iss", titulo: "ISS", contagem: "iss" },
] as const;

export default function FiscalReplication() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [emitenteOrigemId, setEmitenteOrigemId] = useState<number | null>(null);
  const [naturezaOrigemId, setNaturezaOrigemId] = useState<number | null>(null);
  const [nome, setNome] = useState("");
  const [emitentesDestinoIds, setEmitentesDestinoIds] = useState<number[]>([]);
  const [proximosNumeros, setProximosNumeros] = useState<Record<number, string>>({});
  const [familias, setFamilias] = useState<Familias>(FAMILIAS_INICIAIS);

  useEffect(() => {
    if (!authLoading && !user) window.location.href = "/login";
  }, [authLoading, user]);

  const { data: naturezas = [], refetch: refetchNaturezas } = trpc.operationNatures.list.useQuery(undefined, {
    enabled: user?.role === "admin",
  });
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: user?.role === "admin",
  });
  const { data: resumo, isLoading: resumoCarregando } = trpc.fiscalReplication.getResumo.useQuery(
    { naturezaOrigemId: naturezaOrigemId ?? 0 },
    { enabled: user?.role === "admin" && !!naturezaOrigemId }
  );

  const naturezasOrigem = useMemo(
    () => naturezas.filter((natureza: any) => Number(natureza.emitenteId) === emitenteOrigemId),
    [naturezas, emitenteOrigemId]
  );

  const emitentesDestino = useMemo(
    () => emitentes.filter((emitente: any) => emitentesDestinoIds.includes(Number(emitente.id))),
    [emitentes, emitentesDestinoIds]
  );

  const replicarMutation = trpc.fiscalReplication.replicar.useMutation({
    onSuccess: async (resultado) => {
      toast.success(`${resultado.naturezasCriadas.length} natureza(s) criada(s) com as configurações selecionadas.`);
      setEmitenteOrigemId(null);
      setNaturezaOrigemId(null);
      setNome("");
      setEmitentesDestinoIds([]);
      setProximosNumeros({});
      setFamilias(FAMILIAS_INICIAIS);
      await refetchNaturezas();
    },
    onError: (erro) => toast.error(erro.message || "Não foi possível concluir a replicação fiscal."),
  });

  const alternarEmitente = (emitenteId: number, selecionado: boolean) => {
    setEmitentesDestinoIds((atual) => selecionado ? [...atual, emitenteId] : atual.filter((id) => id !== emitenteId));
    if (!selecionado) {
      setProximosNumeros((atual) => {
        const proximo = { ...atual };
        delete proximo[emitenteId];
        return proximo;
      });
    }
  };

  const confirmarReplicacao = () => {
    const nomeTratado = nome.trim();
    if (!naturezaOrigemId) return toast.error("Selecione a Natureza de Operação origem.");
    if (!nomeTratado) return toast.error("Informe o nome da nova Natureza de Operação.");
    if (emitentesDestino.length === 0) return toast.error("Selecione ao menos um emitente destino.");

    const destinos = emitentesDestino.map((emitente: any) => ({
      emitenteId: Number(emitente.id),
      nfeProximoNumero: Number(proximosNumeros[Number(emitente.id)]),
    }));
    if (destinos.some((destino) => !Number.isInteger(destino.nfeProximoNumero) || destino.nfeProximoNumero < 1)) {
      return toast.error("Informe um Próximo Número de NF-e inteiro e maior que zero para cada emitente destino.");
    }
    if (!window.confirm(`Serão criadas ${destinos.length} nova(s) Natureza(s). As regras CFOP/CSOSN acompanharão a Natureza e apenas as famílias fiscais marcadas serão copiadas. Deseja continuar?`)) return;

    replicarMutation.mutate({ naturezaOrigemId, nome: nomeTratado, familias, destinos });
  };

  if (authLoading) return <div className="p-6 text-sm text-muted-foreground">Carregando...</div>;
  if (!user || user.role !== "admin") {
    return <div className="p-6"><Card className="max-w-xl"><CardHeader><CardTitle>Acesso restrito</CardTitle><CardDescription>Este módulo é exclusivo para administradores.</CardDescription></CardHeader></Card></div>;
  }

  const todosEmitentesSelecionados = emitentes.length > 0 && emitentesDestinoIds.length === emitentes.length;
  const todasFamiliasSelecionadas = FAMILIAS_FISCAIS.every((familia) => familias[familia.chave]);

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Replicação Fiscal</h1>
        <p className="text-sm text-muted-foreground mt-1">Crie uma nova Natureza de Operação por emitente destino, a partir de uma Natureza existente.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Origem e nova Natureza</CardTitle>
          <CardDescription>Série e Ambiente NF-e são copiados literalmente da origem. O Próximo Número de NF-e é informado por destino.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="emitente-origem">Emitente origem</Label>
            <select id="emitente-origem" value={emitenteOrigemId ?? ""} onChange={(event) => {
              setEmitenteOrigemId(event.target.value ? Number(event.target.value) : null);
              setNaturezaOrigemId(null);
            }} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Selecione o emitente origem</option>
              {emitentes.map((emitente: any) => <option key={emitente.id} value={emitente.id}>{emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="natureza-origem">Natureza de Operação origem</Label>
            <select id="natureza-origem" disabled={!emitenteOrigemId} value={naturezaOrigemId ?? ""} onChange={(event) => setNaturezaOrigemId(event.target.value ? Number(event.target.value) : null)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50">
              <option value="">Selecione a Natureza origem</option>
              {naturezasOrigem.map((natureza: any) => <option key={natureza.id} value={natureza.id}>{natureza.nome}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nome-nova-natureza">Nome da nova Natureza</Label>
            <Input id="nome-nova-natureza" value={nome} onChange={(event) => setNome(event.target.value)} placeholder="Informe o nome que será criado nos destinos" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Emitentes destino</CardTitle>
          <CardDescription>Selecione um ou mais emitentes e informe o Próximo Número de NF-e de cada um.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer w-fit">
            <input type="checkbox" checked={todosEmitentesSelecionados} onChange={(event) => {
              setEmitentesDestinoIds(event.target.checked ? emitentes.map((emitente: any) => Number(emitente.id)) : []);
              if (!event.target.checked) setProximosNumeros({});
            }} className="h-4 w-4" />
            Selecionar todos
          </label>
          <div className="border rounded-md divide-y">
            {emitentes.map((emitente: any) => {
              const emitenteId = Number(emitente.id);
              const selecionado = emitentesDestinoIds.includes(emitenteId);
              return <div key={emitente.id} className="p-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px] items-center">
                <label className="flex items-center gap-2 text-sm cursor-pointer min-w-0">
                  <input type="checkbox" checked={selecionado} onChange={(event) => alternarEmitente(emitenteId, event.target.checked)} className="h-4 w-4 shrink-0" />
                  <span className="truncate">{emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}</span>
                </label>
                {selecionado && <div className="space-y-1">
                  <Label htmlFor={`nfe-${emitente.id}`} className="text-xs">Próximo Número de NF-e</Label>
                  <Input id={`nfe-${emitente.id}`} type="number" min="1" step="1" value={proximosNumeros[emitenteId] || ""} onChange={(event) => setProximosNumeros((atual) => ({ ...atual, [emitenteId]: event.target.value }))} />
                </div>}
              </div>;
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configurações a replicar</CardTitle>
          <CardDescription>As regras CFOP/CSOSN vinculadas à Natureza acompanham obrigatoriamente a cópia. As demais famílias são selecionadas abaixo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!naturezaOrigemId ? <div className="text-sm text-muted-foreground">Selecione a Natureza origem para ver as quantidades reais.</div> : resumoCarregando ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando prévia...</div> : resumo ? <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md border bg-muted/30 p-3"><div className="text-xs text-muted-foreground">Regras CFOP / CSOSN</div><div className="font-semibold mt-1">{resumo.contagens.regras}</div><div className="text-xs text-muted-foreground mt-1">Obrigatórias</div></div>
              {FAMILIAS_FISCAIS.map((familia) => <label key={familia.chave} className="rounded-md border p-3 flex gap-3 cursor-pointer hover:bg-muted/30">
                <input type="checkbox" checked={familias[familia.chave]} onChange={(event) => setFamilias((atual) => ({ ...atual, [familia.chave]: event.target.checked }))} className="h-4 w-4 mt-0.5" />
                <span><span className="block text-sm font-medium">{familia.titulo}</span><span className="block text-xs text-muted-foreground mt-1">{resumo.contagens[familia.contagem]} registro(s) vinculado(s)</span></span>
              </label>)}
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer w-fit">
              <input type="checkbox" checked={todasFamiliasSelecionadas} onChange={(event) => {
                const selecionado = event.target.checked;
                setFamilias({ icms: selecionado, ipi: selecionado, pisCofins: selecionado, fcp: selecionado, mvaSt: selecionado, cbsIbs: selecionado, iss: selecionado });
              }} className="h-4 w-4" />
              Selecionar todas as famílias fiscais
            </label>
          </> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Prévia da replicação</CardTitle><CardDescription>Nenhum registro existente será alterado ou removido.</CardDescription></CardHeader>
        <CardContent>
          {emitentesDestino.length === 0 ? <div className="text-sm text-muted-foreground">Selecione os emitentes destino para visualizar as Naturezas que serão criadas.</div> : <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left"><tr><th className="p-3 font-medium">Emitente destino</th><th className="p-3 font-medium">Nova Natureza</th><th className="p-3 font-medium">Próximo NF-e</th><th className="p-3 font-medium">Configurações</th></tr></thead>
              <tbody className="divide-y">{emitentesDestino.map((emitente: any) => {
                const emitenteId = Number(emitente.id);
                const linhas = resumo ? [`Regras: ${resumo.contagens.regras}`, ...FAMILIAS_FISCAIS.filter((familia) => familias[familia.chave]).map((familia) => `${familia.titulo}: ${resumo.contagens[familia.contagem]}`)] : ["Aguardando seleção da origem"];
                return <tr key={emitente.id}><td className="p-3">{emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}</td><td className="p-3">{nome.trim() || "—"}</td><td className="p-3">{proximosNumeros[emitenteId] || "—"}</td><td className="p-3 text-xs text-muted-foreground">{linhas.join(" · ")}</td></tr>;
              })}</tbody>
            </table>
          </div>}
          <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>A confirmação cria uma Natureza nova por destino. Se uma cópia falhar, nenhuma Natureza ou regra nova será mantida.</span></div>
          <div className="mt-5 flex justify-end"><Button onClick={confirmarReplicacao} disabled={replicarMutation.isPending}>{replicarMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}Confirmar replicação fiscal</Button></div>
        </CardContent>
      </Card>
    </div>
  );
}
